import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MessageBar, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import { ChevronDown16Regular, Clipboard24Regular, Delete20Regular, Info24Regular, Save20Regular, Send20Regular } from "@fluentui/react-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FileReference } from "../OiReport";
import { fadeIn } from "../EventReport/OiForm";
import OiLoadingSpinner from "../OiLoadingSpinner";
import { email } from "../../../App";
import { useAnalysisOrder } from "../redux/ReduxContext";
import {
  AnalysisOrder,
  AnalysisOrderPriority,
  AnalysisOrderSample,
  PreparationRoutingItem,
} from "../redux/analysisOrder";
import { CreateOiAnalysisOrder, GetDefaultTaggedStaff, GetOiAnalysisOrder, UpdateOiAnalysisOrder } from "../../../services/AnalysisOrderService";
import { getAllOiPowderOrdersGrouped, getPowderOrdersFiltered } from "../../../services/PowderOrderService";
import AnalysisOrderComment from "./AnalysisOrderComment";
import AnalysisDatePicker from "./AnalysisDatePicker";
import AnalysisOrderTags from "./AnalysisOrderTags";
import AnalysisOrderStaff from "./AnalysisOrderStaff";
import AnalysisOrderFiles from "./AnalysisOrderFiles";
import { buildNextSampleId, buildSampleId, defaultRouteSelection, flattenPowderOrderOptions, normalizePreparationAndRoutingData, normalizeSamples, PowderOrderLookupOption } from "./utils";
import { ANALYSIS_DEFINITIONS, AnalysisDefinition, definitionNeedsPreparation, getAnalysisDefinition, SAMPLE_PREPARATION_OPTIONS } from "./analysisConfig";
import { MECHANICAL_PREPARATION_KEY } from "./resultOrderSelection";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { DecimalInput } from "../../DecimalInput";
import { parseDecimal, toDisplayDecimal } from "../../../utils/decimal";
import { ANALYSIS_ORDER_STEP_LABELS, AnalysisOrderSteps } from "./analysisOrderSteps";
import { useStickySummaryTop } from "./useStickySummaryTop";

interface AnalysisOrderFormProps {
  onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

const inputClassName = "mt-1 h-10 w-full border border-primary-dark/70 dark:border-primary-light/50 bg-primary-light dark:bg-oi-dark-background px-3 text-sm text-primary-dark dark:text-oi-powder-form-yellow focus:outline-none";
const textareaClassName = "mt-1 w-full border border-primary-dark/70 dark:border-primary-light/50 bg-primary-light dark:bg-oi-dark-background px-3 py-2 text-sm text-primary-dark dark:text-oi-powder-form-yellow focus:outline-none";
const labelClassName = "grid gap-1 text-sm font-semibold text-primary-dark dark:text-primary-light";
const sharedAnalysisLayoutClassName = "grid items-start gap-4 xl:grid-cols-[1.15fr,0.85fr]";
const reviewStepWidthClassName = "xl:w-[57.5%]";
const sectionTitleClassName = "text-lg font-bold leading-none";

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-white/20 pt-4 first:border-t-0 first:pt-0">
      <div className="mb-2 text-sm font-bold text-primary-dark dark:text-primary-light">{title}</div>
      {children}
    </div>
  );
}

function StepPanel({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`self-start bg-white dark:bg-oi-dark-background p-4 text-primary-dark dark:text-primary-light drop-shadow-md dark:drop-shadow-none ${className}`}>{children}</div>;
}

function ReviewCard({ title, value, className = "" }: { title: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white dark:bg-oi-dark-background p-3 text-primary-dark dark:text-primary-light ${className}`}>
      <div className="text-sm font-bold text-primary-dark dark:text-primary-light">{title}</div>
      <div className="mt-1 text-base text-oi-button-blue dark:text-oi-powder-form-yellow">{value || "-"}</div>
    </div>
  );
}

function SelectionPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick?: () => void;
}) {
  const className = selected
    ? "border-oi-button-blue bg-oi-button-blue text-primary-light"
    : "border-primary-dark/70 dark:border-oi-button-blue bg-transparent text-primary-dark dark:text-primary-light";

  if (!onClick) {
    return <div className={`rounded-full border px-4 py-2 text-sm ${className}`}>{label}</div>;
  }

  return (
    <button type="button" className={`rounded-full border px-4 py-2 text-sm transition-colors ${className}`} onClick={onClick}>
      {label}
    </button>
  );
}

export default function AnalysisOrderForm({ onOpenFileGallery: _onOpenFileGallery }: AnalysisOrderFormProps) {
  const { state, dispatch } = useAnalysisOrder();
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const location = useLocation();
  const { key } = location;
  const [createdAt, setCreatedAt] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [mode, setMode] = useState<"Create" | "Edit" | "Clone">("Create");
  const [isInitializing, setIsInitializing] = useState(true);
  const [step, setStep] = useState(0);
  const [powderLookupQuery, setPowderLookupQuery] = useState("");
  const [powderOptions, setPowderOptions] = useState<PowderOrderLookupOption[]>([]);
  const [showPowderDropdown, setShowPowderDropdown] = useState(false);
  const hasLoaded = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hydrationTokenRef = useRef(0);
  const summaryRef = useRef<HTMLElement>(null);

  const updateField = useCallback(<K extends keyof AnalysisOrder>(field: K, value: AnalysisOrder[K]) => {
    dispatch({ type: "SET_FIELD", payload: { field, value } });
  }, [dispatch]);

  const hydrateForm = useCallback(async (nextUuid: string, hydrationToken: number) => {
    try {
      const order = await GetOiAnalysisOrder(nextUuid);
      if (!order || hydrationTokenRef.current !== hydrationToken) {
        return;
      }

      setCreatedAt(order.createdAt || "");
      setLastUpdated(order.lastUpdated || "");
      const data = JSON.parse(order.jsonData) as AnalysisOrder;
      dispatch({ type: "HYDRATE", payload: data });
      dispatch({ type: "SET_FIELD", payload: { field: "orderId", value: order.orderId || "" } });
      setPowderLookupQuery(data.linkedPowderOrderLabel || data.linkedPowderOrder || "");
    } catch (error) {
      console.error("Error fetching analysis order:", error);
    } finally {
      if (hydrationTokenRef.current === hydrationToken) {
        setIsInitializing(false);
      }
    }
  }, [dispatch]);

  useLayoutEffect(() => {
    const hydrationToken = hydrationTokenRef.current + 1;
    hydrationTokenRef.current = hydrationToken;

    const initializeForm = async () => {
      setIsInitializing(true);
      setStep(0);
      setShowPowderDropdown(false);
      setPowderOptions([]);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      hasLoaded.current = false;

      if (location.pathname === `/Oi/analysisOrderEdit/${uuid}`) {
        setMode("Edit");
        if (uuid) {
          await hydrateForm(uuid, hydrationToken);
          return;
        }
      } else if (location.pathname === `/Oi/analysisOrderClone/${uuid}`) {
        setMode("Clone");
        if (uuid) {
          await hydrateForm(uuid, hydrationToken);
          dispatch({ type: "SET_FIELD", payload: { field: "orderId", value: "" } });
          return;
        }
      } else {
        // New orders are pre-tagged with the "Analysis order receiver" role holders. They are loaded before the form is
        // reset (and the mode switched), so the autosave that creates the draft never runs without them.
        const defaultStaff = await GetDefaultTaggedStaff();
        if (hydrationTokenRef.current !== hydrationToken) {
          return;
        }

        setMode("Create");
        dispatch({
          type: "CLEAR_STATE",
          payload: {
            author: email || "",
            requestedBy: email || "",
            orderDate: new Date().toISOString().slice(0, 10),
            taggedStaff: defaultStaff,
          },
        });
        setPowderLookupQuery("");
      }

      if (hydrationTokenRef.current === hydrationToken) {
        setIsInitializing(false);
      }
    };

    initializeForm();

    return () => {
      if (hydrationTokenRef.current === hydrationToken) {
        hydrationTokenRef.current += 1;
      }
    };
  }, [dispatch, hydrateForm, key, location.pathname, uuid]);

  const selectedAnalyses = useMemo(() => state.selectedAnalysesJsonData?.selectedAnalyses ?? [], [state.selectedAnalysesJsonData]);
  const preparationAndRouting = useMemo(
    () => normalizePreparationAndRoutingData(state.preparationAndRoutingJsonData, selectedAnalyses),
    [selectedAnalyses, state.preparationAndRoutingJsonData],
  );

  useEffect(() => {
    const timeout = setTimeout(async () => {
      try {
        const grouped = powderLookupQuery.trim().length >= 3
          ? await getPowderOrdersFiltered(powderLookupQuery)
          : await getAllOiPowderOrdersGrouped();
        setPowderOptions(flattenPowderOrderOptions(grouped));
      } catch (error) {
        console.error("Failed to load powder order lookup options", error);
        setPowderOptions([]);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [powderLookupQuery]);

  const serializeState = useCallback((isDraft: boolean, submitForReview: boolean) => {
    const normalizedSamples = normalizeSamples(state.linkedPowderOrderLabel || state.linkedPowderOrder, state.samples);
    const stateToSave: AnalysisOrder = {
      ...state,
      author: email,
      requestedBy: state.requestedBy || email,
      isDraft,
      isSubmittedForReview: submitForReview,
      reviewPassed: isDraft ? null : state.reviewPassed,
      samples: normalizedSamples,
      preparationAndRoutingJsonData: normalizePreparationAndRoutingData(state.preparationAndRoutingJsonData, selectedAnalyses),
    };

    return JSON.stringify(stateToSave);
  }, [selectedAnalyses, state]);

  const autoSave = useCallback(async () => {
    const payload = serializeState(true, false);

    if (mode === "Edit" && uuid) {
      await UpdateOiAnalysisOrder(uuid, payload, true, false, null);
      return;
    }

    if (mode === "Create" || mode === "Clone") {
      const response = await CreateOiAnalysisOrder(payload, true, email, false, null);
      const parsed = JSON.parse(response || "{}");
      if (parsed.uuid) {
        navigate(`/Oi/analysisOrderEdit/${parsed.uuid}`);
      }
    }
  }, [mode, navigate, serializeState, uuid]);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      autoSave();
    }, 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [autoSave, state]);

  const save = async (isDraft: boolean) => {
    const payload = serializeState(isDraft, !isDraft);

    if ((mode === "Create" || mode === "Clone")) {
      const response = await CreateOiAnalysisOrder(payload, isDraft, email, !isDraft, isDraft ? null : state.reviewPassed);
      const parsed = JSON.parse(response || "{}");
      if (parsed.uuid) {
        navigate(`/Oi/analysisOrder/${parsed.uuid}`);
      }
      return;
    }

    if (mode === "Edit" && uuid) {
      await UpdateOiAnalysisOrder(uuid, payload, isDraft, !isDraft, isDraft ? null : state.reviewPassed);
      navigate(`/Oi/analysisOrder/${uuid}`);
    }
  };

  const addSample = () => {
    // Without a linked order, whatever is typed in the lookup field is used as the sample prefix.
    const linkedPowderOrderLabel = (state.linkedPowderOrderLabel || state.linkedPowderOrder || "").trim();
    const nextSample: AnalysisOrderSample = {
      sampleId: linkedPowderOrderLabel ? buildNextSampleId(linkedPowderOrderLabel, state.samples) : "",
      linkedPowderOrderId: linkedPowderOrderLabel,
      coPercentage: null,
    };

    dispatch({ type: "ADD_ARRAY_ITEM_LAST", payload: { field: "samples", item: nextSample } });
  };

  const updateSample = (index: number, updates: Partial<AnalysisOrderSample>) => {
    dispatch({
      type: "UPDATE_ARRAY_ITEM",
      payload: {
        field: "samples",
        index,
        value: (current: AnalysisOrderSample) => ({ ...current, ...updates }),
      },
    });
  };

  const removeSample = (index: number) => {
    dispatch({ type: "DELETE_ARRAY_ITEM", payload: { field: "samples", index } });
  };

  const toggleAnalysis = (analysisKey: string) => {
    const current = state.selectedAnalysesJsonData?.selectedAnalyses ?? [];
    const nextSelected = current.includes(analysisKey)
      ? current.filter(item => item !== analysisKey)
      : [...current, analysisKey];

    updateField("selectedAnalysesJsonData", { selectedAnalyses: nextSelected });

    const nextPreparationAndRouting = normalizePreparationAndRoutingData(state.preparationAndRoutingJsonData, current);
    const nextRoutes = { ...nextPreparationAndRouting.routes };
    if (!nextSelected.includes(analysisKey)) {
      delete nextRoutes[analysisKey];
    } else if (!nextRoutes[analysisKey]) {
      nextRoutes[analysisKey] = defaultRouteSelection(analysisKey);
    }

    updateField("preparationAndRoutingJsonData", {
      ...nextPreparationAndRouting,
      routes: nextRoutes,
    });
  };

  const updatePreparationItem = (analysisKey: string, updater: (current: PreparationRoutingItem) => PreparationRoutingItem) => {
    const currentRoutes = { ...preparationAndRouting.routes };
    currentRoutes[analysisKey] = updater(currentRoutes[analysisKey] ?? { selectedOptions: [] });

    updateField("preparationAndRoutingJsonData", {
      ...preparationAndRouting,
      routes: currentRoutes,
    });
  };

  const selectedAnalysisDetails = useMemo(() => {
    return selectedAnalyses
      .map(analysisKey => getAnalysisDefinition(analysisKey) ?? {
        key: analysisKey,
        title: analysisKey,
        cardDescription: "",
        routeOptions: [],
      })
      .filter(Boolean) as AnalysisDefinition[];
  }, [selectedAnalyses]);

  const selectedAnalysesRequirePreparation = useMemo(
    () => selectedAnalysisDetails.some(definitionNeedsPreparation),
    [selectedAnalysisDetails],
  );

  const preparationChips = useMemo(
    () => (selectedAnalysesRequirePreparation && !preparationAndRouting.noPreparationRequired ? preparationAndRouting.selectedPreparationMethods : []),
    [preparationAndRouting.noPreparationRequired, preparationAndRouting.selectedPreparationMethods, selectedAnalysesRequirePreparation],
  );

  const reviewAnalysisCards = useMemo(() => {
    const cards: Array<{ title: string; value: string }> = [];

    const visibleAnalysisDetails = selectedAnalysisDetails.filter(option => !option.preparationOnly);

    visibleAnalysisDetails.forEach(option => {
      const selectedOptions = preparationAndRouting.routes[option.key]?.selectedOptions ?? [];
      cards.push({
        title: option.title.replace(/\s+analysis$/i, ""),
        value: selectedOptions.length > 0 ? selectedOptions.join(", ") : "No route options selected",
      });
    });

    if (selectedAnalysesRequirePreparation) {
      cards.splice(1, 0, {
        title: "Selected preparation method",
        value: preparationAndRouting.noPreparationRequired
          ? "No preparation required"
          : (preparationAndRouting.selectedPreparationMethods.length > 0
            ? preparationAndRouting.selectedPreparationMethods.join(", ")
            : "No preparation method selected"),
      });
    }

    return cards;
  }, [preparationAndRouting.noPreparationRequired, preparationAndRouting.routes, preparationAndRouting.selectedPreparationMethods, selectedAnalysisDetails, selectedAnalysesRequirePreparation]);

  const togglePreparationMethod = (preparationMethod: string) => {
    const nextSelectedPreparationMethods = preparationAndRouting.selectedPreparationMethods.includes(preparationMethod)
      ? preparationAndRouting.selectedPreparationMethods.filter(method => method !== preparationMethod)
      : [...preparationAndRouting.selectedPreparationMethods, preparationMethod];

    updateField("preparationAndRoutingJsonData", {
      ...preparationAndRouting,
      noPreparationRequired: false,
      selectedPreparationMethods: nextSelectedPreparationMethods,
    });
  };

  const toggleRouteOption = (analysisKey: string, routeOption: string) => {
    updatePreparationItem(analysisKey, current => ({
      selectedOptions: current.selectedOptions.includes(routeOption)
        ? current.selectedOptions.filter(option => option !== routeOption)
        : [...current.selectedOptions, routeOption],
    }));
  };

  const selectPowderOrder = (option: PowderOrderLookupOption) => {
    setPowderLookupQuery(option.orderId);
    updateField("linkedPowderOrder", option.uuid);
    updateField("linkedPowderOrderLabel", option.orderId);
    setShowPowderDropdown(false);
  };

  useStickySummaryTop(summaryRef, `${step}-${isInitializing}`);

  const renderOrderSummary = () => (
    <aside ref={summaryRef} aria-label="Order summary" className="self-start bg-white dark:bg-oi-dark-background p-4 text-primary-dark dark:text-primary-light drop-shadow-md dark:drop-shadow-none xl:sticky xl:top-[var(--order-summary-top,0px)] xl:max-h-[calc(100vh-var(--order-summary-top,0px)-0.75rem)] xl:overflow-y-auto">
      <div className="mb-5 text-lg font-bold leading-none">Order summary</div>

      <SummarySection title="Order information">
        <div className="grid gap-y-3 text-sm md:grid-cols-[180px,1fr] md:gap-x-6">
          <div className="text-primary-dark/80 dark:text-primary-light">Order ID</div>
          <div>{state.orderId || "Draft"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Linked powder order</div>
          <div>{state.linkedPowderOrderLabel || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Requested by</div>
          <div>{state.requestedBy || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Requester department</div>
          <div>{state.requesterDepartment || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Order date</div>
          <div>{state.orderDate || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Requested delivery date</div>
          <div>{state.requestedDeliveryDate || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Priority</div>
          <div>{state.priority || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Reason for higher priority</div>
          <div>{state.reasonForHigherPriority || "Not set"}</div>
          <div className="text-primary-dark/80 dark:text-primary-light">Return material?</div>
          <div>{state.returnMaterial ? "Yes" : "No"}</div>
        </div>
        {state.uploadedFiles.length > 0 && (
          <div className="mt-3 grid gap-y-1 text-sm md:grid-cols-[180px,1fr] md:gap-x-6">
            <div className="text-primary-dark/80 dark:text-primary-light">Attached files</div>
            <ul className="grid min-w-0 gap-1">
              {state.uploadedFiles.map((file, index) => (
                <li key={file.uuid} className="min-w-0">
                  <button type="button" className="max-w-full truncate text-left text-oi-button-blue underline dark:text-blue-300" title={file.fileName} onClick={() => _onOpenFileGallery?.(state.uploadedFiles, index)}>{file.fileName}</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </SummarySection>

      <SummarySection title={`Samples (${state.samples.length})`}>
        <div className="grid gap-y-4 text-sm md:grid-cols-[1fr,120px] md:gap-x-6">
          {normalizeSamples(state.linkedPowderOrderLabel || state.linkedPowderOrder, state.samples).map(sample => (
            <React.Fragment key={sample.sampleId}>
              <div>{sample.sampleId}</div>
              <div>{sample.coPercentage == null ? "Co % -" : `Co % ${toDisplayDecimal(sample.coPercentage)}`}</div>
            </React.Fragment>
          ))}
          {state.samples.length === 0 && <div className="text-primary-dark/70 dark:text-gray-300">No samples added</div>}
        </div>
      </SummarySection>

      <SummarySection title="Selected analysis groups">
        <div className="grid gap-5">
          {selectedAnalysisDetails.length > 0 ? selectedAnalysisDetails.map(option => {
            const chips = option.preparationOnly ? preparationChips : (preparationAndRouting.routes[option.key]?.selectedOptions ?? []);
            return (
              <div key={option.key}>
                <div className="text-sm text-primary-dark dark:text-primary-light">{option.title}</div>
                {step > 1 && chips.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {chips.map(selection => (
                      <SelectionPill key={selection} label={selection} selected={true} />
                    ))}
                  </div>
                )}
              </div>
            );
          }) : <div className="text-primary-dark/70 dark:text-gray-300">No analysis groups selected</div>}
          {step > 1 && preparationChips.length > 0 && !selectedAnalyses.includes(MECHANICAL_PREPARATION_KEY) && (
            <div>
              <div className="text-sm text-primary-dark dark:text-primary-light">Mechanical preparation</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {preparationChips.map(selection => (
                  <SelectionPill key={selection} label={selection} selected={true} />
                ))}
              </div>
            </div>
          )}
        </div>
      </SummarySection>
    </aside>
  );

  if (isInitializing) {
    return (
      <OiLoadingSpinner />
    );
  }

  return (
    <div className="analysis-order-theme dark:[--analysis-date-border:theme(colors.primary-light/50%)] dark:[--analysis-date-bg:theme(colors.oi-dark-background)] dark:[--analysis-date-text:theme(colors.oi-powder-form-yellow)] dark:[--analysis-date-placeholder:theme(colors.oi-powder-form-yellow)] w-full max-w-none mx-6 pb-4 text-primary-dark dark:text-primary-light" style={{ animation: "fadeIn 1s forwards" }}>
      <style>{`${fadeIn}
        .analysis-order-theme {
          --analysis-date-border: rgb(55 65 81 / 0.7);
          --analysis-date-bg: #ffffff;
          --analysis-date-text: #111827;
          --analysis-date-placeholder: rgb(17 24 39 / 0.65);
        }


      `}</style>
      <div className="w-full">
        <OiPageHeader
          icon={<Clipboard24Regular />}
          title={mode === "Edit" ? "Edit analysis order" : mode === "Clone" ? "Clone analysis order" : "New analysis order"}
          id={mode === "Create" ? undefined : state.orderId}
          createdAt={mode === "Edit" ? createdAt : undefined}
          updatedAt={mode === "Edit" ? lastUpdated : undefined}
          steps={<AnalysisOrderSteps currentStep={step} onStepClick={setStep} />}
          actions={<>
            <OiHeaderButton
              variant="secondary"
              className={step > 0 ? "" : "invisible"}
              onClick={() => setStep(step - 1)}
              tabIndex={step > 0 ? 0 : -1}
              aria-hidden={step > 0 ? undefined : true}
            >
              Back
            </OiHeaderButton>
            <OiHeaderButton variant="secondary" icon={<Save20Regular />} onClick={() => save(true)}>Save draft</OiHeaderButton>
            {step < ANALYSIS_ORDER_STEP_LABELS.length - 1 ? (
              <OiHeaderButton onClick={() => setStep(step + 1)}>Next</OiHeaderButton>
            ) : (
              <OiHeaderButton icon={<Send20Regular />} onClick={() => save(false)}>Submit</OiHeaderButton>
            )}
          </>}
        />

        {step === 0 && (
          <div className={sharedAnalysisLayoutClassName}>
            <div className="grid gap-4">
              <h2 className={sectionTitleClassName}>1. Order information</h2>
              <StepPanel>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className={labelClassName}>
                    <span>Order ID</span>
                    <input className={inputClassName} value={state.orderId || "Auto-generated on save"} disabled />
                  </label>
                  <div className={`${labelClassName} relative`}>
                    <span>Linked Powder order</span>
                    <span className="relative">
                    <input
                      className={`${inputClassName} pr-20`}
                      placeholder="Search powder order"
                      value={powderLookupQuery}
                      onFocus={() => setShowPowderDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPowderDropdown(false), 100)}
                      onChange={(event) => {
                        setPowderLookupQuery(event.target.value);
                        setShowPowderDropdown(true);
                        updateField("linkedPowderOrder", "");
                        updateField("linkedPowderOrderLabel", event.target.value.trim());
                      }}
                    />
                    <button
                      type="button"
                      title="Add sample"
                      className="absolute right-2 top-1/2 mt-0.5 h-7 -translate-y-1/2 px-3 py-1 text-sm bg-oi-button-blue text-primary-light"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={addSample}
                    >
                      Add
                    </button>
                    </span>
                    {showPowderDropdown && powderOptions.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-20 max-h-64 overflow-y-auto border border-primary-dark/70 dark:border-primary-light/50 bg-white dark:bg-oi-dark-background shadow-lg">
                        {powderOptions
                          .filter(option => option.orderId.toLowerCase().includes(powderLookupQuery.toLowerCase()))
                          .slice(0, 20)
                          .map(option => (
                            <button
                              key={option.uuid}
                              type="button"
                              className="block w-full border-b border-primary-dark/20 dark:border-primary-light/50 px-3 py-2 text-left text-sm text-primary-dark dark:text-primary-light hover:bg-primary-dark/10 dark:hover:bg-primary-light/10"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                selectPowderOrder(option);
                              }}
                            >
                              {option.orderId}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                  <label className={labelClassName}>
                    <span>Requested by</span>
                    <input className={inputClassName} value={state.requestedBy} onChange={(event) => updateField("requestedBy", event.target.value)} />
                  </label>
                  <label className={labelClassName}>
                    <span>Requester department</span>
                    <input className={inputClassName} value={state.requesterDepartment} onChange={(event) => updateField("requesterDepartment", event.target.value)} />
                  </label>
                  <label className={labelClassName}>
                    <span>Order date</span>
                    <AnalysisDatePicker value={state.orderDate} onChange={(value) => updateField("orderDate", value)} />
                  </label>
                  <label className={labelClassName}>
                    <span>Requested delivery date</span>
                    <AnalysisDatePicker value={state.requestedDeliveryDate} onChange={(value) => updateField("requestedDeliveryDate", value)} />
                  </label>
                  <label className={labelClassName}>
                    <span>Priority</span>
                    <span className="relative">
                      <select className={`${inputClassName} appearance-none pr-10`} value={state.priority} onChange={(event) => updateField("priority", event.target.value as AnalysisOrderPriority)}>
                        {Object.values(AnalysisOrderPriority).map(priority => <option key={priority} value={priority}>{priority}</option>)}
                      </select>
                      <ChevronDown16Regular aria-hidden className="pointer-events-none absolute right-3 top-1/2 mt-0.5 -translate-y-1/2 text-primary-dark dark:text-primary-light" />
                    </span>
                  </label>
                  <label className={labelClassName}>
                    <span>Reason for higher priority</span>
                    <textarea className={`h-10 resize-none ${textareaClassName}`} value={state.reasonForHigherPriority} onChange={(event) => updateField("reasonForHigherPriority", event.target.value)} />
                  </label>
                </div>
                <div className="mt-6">
                  <div className="mb-2 text-sm font-semibold">Return material?</div>
                  <label className="flex items-center gap-3 text-sm">
                    <input type="checkbox" checked={state.returnMaterial} onChange={(event) => updateField("returnMaterial", event.target.checked)} className="h-4 w-4" />
                    <span>Yes</span>
                  </label>
                </div>

                <div className="mt-8 border-t border-gray-600 pt-4">
                  <div className="mb-2 text-sm font-bold">Samples</div>
                  <div className="grid grid-cols-[2fr,1fr,80px] bg-primary-dark/10 dark:bg-black/40 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-primary-dark dark:text-primary-light">
                    <div>Sample name</div>
                    <div>Co % (if known)</div>
                    <div>Action</div>
                  </div>
                  {state.samples.map((sample, index) => (
                    <div key={index} className="grid grid-cols-[2fr,1fr,80px] items-center border-t border-primary-dark/20 dark:border-primary-light/50 bg-white dark:bg-oi-dark-background px-3 py-2">
                      {sample.linkedPowderOrderId
                        ? <div className="h-9 flex items-center text-sm text-primary-dark dark:text-oi-powder-form-yellow">{sample.sampleId || buildSampleId(sample.linkedPowderOrderId, index)}</div>
                        : <input className="h-9 w-full border border-primary-dark/70 bg-primary-light px-2 text-sm text-primary-dark outline-none focus:border-oi-button-blue focus:ring-1 focus:ring-oi-button-blue dark:border-primary-light/50 dark:bg-oi-dark-background dark:text-oi-powder-form-yellow" placeholder="Enter sample name" value={sample.sampleId} onChange={(event) => updateSample(index, { sampleId: event.target.value })} />}
                      <DecimalInput aria-label={`Co % for sample ${index + 1}`} className="h-9 w-24 border border-primary-dark/70 dark:border-primary-light/50 bg-primary-light dark:bg-oi-dark-background px-2 text-sm text-oi-button-blue dark:text-oi-powder-form-yellow" value={sample.coPercentage} onValueChange={(value) => updateSample(index, { coPercentage: parseDecimal(value) })} />
                      <button type="button" aria-label={`Remove sample ${index + 1}`} title="Remove sample" className="justify-self-center text-gray-300" onClick={() => removeSample(index)}><Delete20Regular /></button>
                    </div>
                  ))}
                </div>
              </StepPanel>
            </div>

            <div className="grid gap-4">
              <AnalysisOrderTags />
              <AnalysisOrderStaff />
              <AnalysisOrderFiles onOpenFileGallery={_onOpenFileGallery} />
              <AnalysisOrderComment />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className={sharedAnalysisLayoutClassName}>
            <div className="grid gap-4">
              <h2 className={sectionTitleClassName}>2. Select analysis groups</h2>
              <StepPanel>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {ANALYSIS_DEFINITIONS.map(option => {
                    const selected = selectedAnalyses.includes(option.key);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        className={`flex min-h-[160px] flex-col items-center justify-center gap-3 border p-4 text-center border-oi-button-blue ${selected ? " bg-oi-button-blue/90 text-primary-light" : " bg-primary-dark/5 dark:bg-oi-dark-background text-primary-dark dark:text-primary-light"}`}
                        onClick={() => toggleAnalysis(option.key)}
                      >
                        <span className="text-lg font-bold leading-tight">{option.title}</span>
                        <span className="max-w-[16rem] text-sm leading-relaxed text-primary-dark/80 dark:text-primary-light">{option.cardDescription}</span>
                      </button>
                    );
                  })}
                </div>
              </StepPanel>
            </div>
            {renderOrderSummary()}
          </div>
        )}

        {step === 2 && (
          <div className={sharedAnalysisLayoutClassName}>
            <div className="grid gap-6">
              <h2 className={sectionTitleClassName}>3. Preparation and routing</h2>
              {selectedAnalysesRequirePreparation && (
                <MessageBar
                  intent="info"
                  layout="multiline"
                  icon={{
                    children: <Info24Regular />,
                    style: { color: "#60a5fa", display: "flex", alignItems: "center" },
                  }}
                  className="border text-white backdrop-blur-sm"
                  style={{ backgroundColor: "rgba(6, 108, 234, 0.28)", borderColor: "#60a5fa", color: "#ffffff" }}
                >
                  <MessageBarBody className="text-white" style={{ color: "#ffffff" }}>
                    <MessageBarTitle className="text-white" style={{ color: "#ffffff" }}>Sample preparation required</MessageBarTitle>
                    The selected analyses require sample preparation. The order will first be routed to Sample preparation.
                  </MessageBarBody>
                </MessageBar>
              )}

              <div className=" text-primary-dark dark:text-primary-light drop-shadow-md dark:drop-shadow-none">
                {selectedAnalysesRequirePreparation && (
                    <div className="mb-6 bg-white dark:bg-oi-dark-background p-4 drop-shadow-md dark:drop-shadow-none">
                    <h3 className="text-sm font-bold">1. Sample preparation {preparationAndRouting.noPreparationRequired ? "(skipped)" : "(required)"}</h3>
                    <p className="mt-2 text-sm text-primary-dark/80 dark:text-primary-light">
                      The selected analyses ({selectedAnalysisDetails.filter(definitionNeedsPreparation).map(option => option.title).join("; ")}) require sample preparation.
                    </p>
                    <div className="mt-4 text-sm font-semibold uppercase tracking-wide">Selected preparation method</div>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {SAMPLE_PREPARATION_OPTIONS.map(preparationMethod => (
                        <SelectionPill
                          key={preparationMethod}
                          label={preparationMethod}
                          selected={preparationAndRouting.selectedPreparationMethods.includes(preparationMethod)}
                          onClick={() => togglePreparationMethod(preparationMethod)}
                        />
                      ))}
                    </div>
                    <MessageBar
                      intent="info"
                      layout="multiline"
                      icon={{
                        children: <Info24Regular />,
                        style: { color: "#60a5fa", display: "flex", alignItems: "center" },
                      }}
                      className="mt-4 border text-white backdrop-blur-sm"
                      style={{ backgroundColor: "rgba(6, 108, 234, 0.28)", borderColor: "#60a5fa", color: "#ffffff" }}
                    >
                      <MessageBarBody className="text-white" style={{ color: "#ffffff" }}>
                        <MessageBarTitle className="text-white" style={{ color: "#ffffff" }}>No preparation option</MessageBarTitle>
                        If you choose "No preparation required", the sample will be sent directly to the selected analysis groups.
                      </MessageBarBody>
                    </MessageBar>
                    <label className="mt-4 flex items-center gap-3 text-sm">
                      <input
                        type="checkbox"
                        checked={preparationAndRouting.noPreparationRequired}
                        onChange={(event) => updateField("preparationAndRoutingJsonData", {
                          ...preparationAndRouting,
                          noPreparationRequired: event.target.checked,
                          selectedPreparationMethods: event.target.checked ? [] : preparationAndRouting.selectedPreparationMethods,
                        })}
                        className="h-4 w-4"
                      />
                      <span>No preparation required. Send sample directly to selected analysis groups.</span>
                    </label>
                  </div>
                )}

                <div className="bg-white dark:bg-oi-dark-background p-4 drop-shadow-md dark:drop-shadow-none">
                  <h3 className="text-sm font-bold">2. Routing to analysis group</h3>
                  <p className="mt-2 text-sm text-primary-dark/80 dark:text-primary-light">
                    After preparation is completed, the sample will automatically routed to the selected analysis groups.
                  </p>
                  <div className="mt-4 grid gap-5">
                    {selectedAnalysisDetails.filter(option => !option.preparationOnly && option.routeOptions.length > 0).map(option => {
                      const routeState = preparationAndRouting.routes[option.key] ?? { selectedOptions: [] };
                      return (
                        <div key={option.key} className="border-t border-white/20 pt-4 first:border-t-0 first:pt-0">
                          <div className="text-sm font-semibold uppercase tracking-wide">{option.title}</div>
                          <div className="mt-3 flex flex-wrap gap-3">
                            {option.routeOptions.map(routeOption => (
                              <SelectionPill
                                key={routeOption}
                                label={routeOption}
                                selected={routeState.selectedOptions.includes(routeOption)}
                                onClick={() => toggleRouteOption(option.key, routeOption)}
                              />
                            ))}
                          </div>
                          {option.guidanceNotes && option.guidanceNotes.length > 0 && (
                            <div className="mt-3 grid gap-1 text-xs text-primary-dark/70 dark:text-primary-light/70">
                              {option.guidanceNotes.map(note => <div key={note}>{note}</div>)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 bg-white dark:bg-oi-dark-background p-4 drop-shadow-md dark:drop-shadow-none">
                  <h3 className="text-sm font-bold">3. Comment (optional)</h3>
                  <p className="mt-2 text-sm text-primary-dark/80 dark:text-primary-light/70">Add any information for the preparation or analysis teams.</p>
                  <textarea
                    className="mt-3 min-h-24 w-full border border-primary-dark/70 dark:border-primary-light/50 bg-primary-light dark:bg-oi-dark-background px-3 py-2 text-sm text-primary-dark dark:text-oi-powder-form-yellow"
                    value={preparationAndRouting.comment}
                    onChange={(event) => updateField("preparationAndRoutingJsonData", {
                      ...preparationAndRouting,
                      comment: event.target.value,
                    })}
                  />
                </div>
              </div>
            </div>
            {renderOrderSummary()}
          </div>
        )}

        {step === 3 && (
          <div className={`grid gap-4 ${reviewStepWidthClassName}`}>
            <h2 className={sectionTitleClassName}>4. Review and submit</h2>
            <div className="grid gap-3 bg-white/[0.03] p-[1px] drop-shadow-md dark:drop-shadow-none">
              <div className="grid gap-2 bg-white/[0.03] p-[1px] md:grid-cols-2">
                <ReviewCard title="Order id" value={state.orderId || "Draft"} />
                <ReviewCard title="Linked Powder order" value={state.linkedPowderOrderLabel || "Not set"} />
                <ReviewCard title="Requested by" value={state.requestedBy || "Not set"} />
                <ReviewCard title="Requester department" value={state.requesterDepartment || "Not set"} />
                <ReviewCard title="Order date" value={state.orderDate || "Not set"} />
                <ReviewCard title="Requested delivery date" value={state.requestedDeliveryDate || "Not set"} />
                <ReviewCard title="Priority" value={state.priority} />
                <ReviewCard title="Reason for higher priority" value={state.reasonForHigherPriority || "Not set"} />
              </div>

              <div className="bg-primary-dark/10 px-3 py-2 text-sm font-bold text-primary-dark dark:bg-black/20 dark:text-primary-light">
                Samples
              </div>
              <div className="grid grid-cols-[2fr,1fr] bg-white dark:bg-oi-dark-background px-3 py-2 text-xs text-primary-dark dark:text-primary-light">
                <div>Sample name</div>
                <div className="text-right">Co % (if known)</div>
              </div>
              {(state.samples.length ? normalizeSamples(state.linkedPowderOrderLabel || state.linkedPowderOrder, state.samples) : []).map(sample => (
                <div key={sample.sampleId} className="grid grid-cols-[2fr,1fr] bg-white dark:bg-oi-dark-background px-3 py-3 text-oi-button-blue dark:text-oi-powder-form-yellow">
                  <div>{sample.sampleId}</div>
                  <div className="text-right">{sample.coPercentage == null ? "-" : toDisplayDecimal(sample.coPercentage)}</div>
                </div>
              ))}

                <div className="grid gap-2 bg-white/[0.03] p-[1px] md:grid-cols-2">
                  <ReviewCard title="Return material" value={state.returnMaterial ? "Yes" : "No"} />
                  <ReviewCard title="Comment:" value={state.comment || "Not set"} />
                  <ReviewCard title="Tagged staff:" value={state.taggedStaff.join(", ") || "Not set"} />
                  <ReviewCard title="Tags:" value={state.tags.join(", ") || "Not set"} />
                </div>

              <ReviewCard title="Attachments:" value={state.uploadedFiles.length > 0 && (
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {state.uploadedFiles.map((file, index) => (
                    <button key={file.uuid} type="button" className="underline" onClick={() => _onOpenFileGallery?.(state.uploadedFiles, index)}>{file.fileName}</button>
                  ))}
                </span>
              )} />

              <div className="bg-primary-dark/10 px-3 py-2 text-sm font-bold text-primary-dark dark:bg-black/20 dark:text-primary-light">
                Selected analysis
              </div>
              <div className="grid gap-2 bg-white/[0.03] p-[1px] md:grid-cols-2">
                {reviewAnalysisCards.length > 0
                  ? reviewAnalysisCards.map(card => <ReviewCard key={card.title} title={card.title} value={card.value} />)
                  : <ReviewCard title="Selected analysis" value="None selected" className="md:col-span-2" />}
              </div>

              <ReviewCard title="Information for analysis team" value={preparationAndRouting.comment || "Not set"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
